import React, { useState } from 'react';
import './DocumentationPage.css';
import HomeButton from './HomeButton';

const sections = [
  { id: 'getting-started', label: 'Getting Started', icon: '🚀' },
  { id: 'modules', label: 'Modules', icon: '📦' },
  { id: 'projects', label: 'Projects', icon: '📐' },
  { id: 'products', label: 'Products', icon: '🛋️' },
  { id: 'contacts', label: 'Contacts & Leads', icon: '👥' },
  { id: 'chat', label: 'Chat', icon: '💬' },
  { id: 'calendar', label: 'Calendar', icon: '📅' },
];

const sectionContent = {
  'getting-started': {
    title: 'Getting Started',
    content: (
      <>
        <h3>Welcome to JonesCRM</h3>
        <p>JonesCRM is a sophisticated CRM solution tailored for interior designers. This guide will help you get started with the key features of the platform.</p>

        <h4>Navigating the Dashboard</h4>
        <p>After logging in, you'll see the main dashboard with a sidebar on the left. The sidebar contains all available modules based on your permissions. Click any module to open it.</p>

        <h4>Sidebar Customization</h4>
        <p>Click the gear icon at the bottom of the sidebar to customize which modules are visible and their order. You can drag and drop modules to rearrange them, or toggle their visibility with checkboxes.</p>

        <h4>Your Account</h4>
        <p>Click your name in the top-right corner to access Account Settings. From there you can update your profile information and change your password.</p>

        <h4>Quick Tips</h4>
        <ul>
          <li>Use the calendar button (bottom-right) to quickly view and manage your schedule</li>
          <li>Use the chat button to communicate with team members in real time</li>
          <li>Most list views support sorting, searching, and pagination</li>
        </ul>
      </>
    ),
  },
  'modules': {
    title: 'Modules Overview',
    content: (
      <>
        <h3>Understanding Modules</h3>
        <p>JonesCRM organizes your data into modules. Each module represents a type of business record.</p>

        <h4>Core Modules</h4>
        <ul>
          <li><strong>Accounts</strong> — Companies and organizations you work with</li>
          <li><strong>Contacts</strong> — Individual people associated with accounts</li>
          <li><strong>Leads</strong> — Potential clients not yet converted to contacts</li>
          <li><strong>Opportunities</strong> — Potential deals and revenue tracking</li>
        </ul>

        <h4>Business Modules</h4>
        <ul>
          <li><strong>Businesses</strong> — Manage your business entities and types</li>
          <li><strong>Accounting</strong> — Financial records linked to businesses</li>
          <li><strong>Proposals</strong> — Create and send proposals to clients</li>
          <li><strong>Invoices</strong> — Generate invoices from proposals or standalone</li>
        </ul>

        <h4>Working with Records</h4>
        <p>Each module provides a list view with search and pagination. Click a record to view its details. Use the action buttons to create, edit, or delete records.</p>
      </>
    ),
  },
  'projects': {
    title: 'Projects',
    content: (
      <>
        <h3>Project Management</h3>
        <p>The Projects module is designed for managing interior design projects from concept to completion.</p>

        <h4>Creating a Project</h4>
        <p>Click "New Project" to create a project. Fill in the project name, status, priority, start and end dates, and assign it to a team member.</p>

        <h4>Rooms</h4>
        <p>Each project can have multiple rooms. Rooms help you organize the different spaces within a design project.</p>
        <ul>
          <li><strong>Room Notes</strong> — Add notes and observations for each room</li>
          <li><strong>Room Images</strong> — Upload reference photos and inspiration images</li>
          <li><strong>Room Drawings</strong> — Upload floor plans, elevations, and design drawings</li>
        </ul>

        <h4>Project Contacts</h4>
        <p>Associate contacts with your project using the Contacts tab. Search for existing contacts and link them to keep track of all stakeholders.</p>

        <h4>Project Proposals & Invoices</h4>
        <p>Link proposals and invoices to projects to track the financial side of each engagement.</p>
      </>
    ),
  },
  'products': {
    title: 'Products',
    content: (
      <>
        <h3>Product Catalog</h3>
        <p>The Products module manages your catalog of furniture, materials, and design items.</p>

        <h4>Managing Products</h4>
        <p>Each product has a name, description, part number, category, and pricing. You can search and filter the catalog using the search bar.</p>

        <h4>Product Categories</h4>
        <p>Organize products into categories for easier browsing. Categories can be managed from the Products page.</p>

        <h4>Using Products in Proposals</h4>
        <p>When creating a proposal, you can add products as line items. The product price will auto-populate, and you can adjust quantities and discounts.</p>
      </>
    ),
  },
  'contacts': {
    title: 'Contacts & Leads',
    content: (
      <>
        <h3>Managing Contacts & Leads</h3>

        <h4>Contacts</h4>
        <p>Contacts represent individuals you have an established relationship with. Each contact can be linked to an account (company) and associated with projects, opportunities, and other records.</p>
        <ul>
          <li>Store phone numbers, emails, and addresses</li>
          <li>Track communication history</li>
          <li>Link to accounts and projects</li>
        </ul>

        <h4>Leads</h4>
        <p>Leads are potential clients who have shown interest but haven't been qualified yet. Once qualified, a lead can be converted into a Contact and Account.</p>
        <ul>
          <li>Track lead source and status</li>
          <li>Convert leads to contacts when they become clients</li>
          <li>Assign leads to team members for follow-up</li>
        </ul>
      </>
    ),
  },
  'chat': {
    title: 'Chat',
    content: (
      <>
        <h3>Team Chat</h3>
        <p>The built-in chat feature allows real-time communication with your team members.</p>

        <h4>Direct Messages</h4>
        <p>Click the chat icon in the bottom-right corner to open the chat panel. Select a team member to start a direct conversation.</p>

        <h4>Group Chats</h4>
        <p>Create group conversations for project teams or departments. Group admins can manage members and settings.</p>
        <ul>
          <li>Click "New Group" to create a group chat</li>
          <li>Add or remove members from the group settings</li>
          <li>Group messages are visible to all members</li>
        </ul>

        <h4>Notifications</h4>
        <p>Unread message counts appear on the chat icon. The panel will show which conversations have new messages.</p>
      </>
    ),
  },
  'calendar': {
    title: 'Calendar',
    content: (
      <>
        <h3>Calendar & Scheduling</h3>
        <p>The calendar helps you manage meetings, calls, and tasks.</p>

        <h4>Viewing Your Calendar</h4>
        <p>Click the calendar icon in the bottom-right corner to open the calendar panel. It shows your upcoming events and tasks.</p>

        <h4>Creating Events</h4>
        <p>Events are managed through the Meetings and Calls modules. You can create meetings and calls that will appear on your calendar.</p>

        <h4>Tasks</h4>
        <p>Tasks with due dates will also appear on your calendar, helping you stay on top of deadlines.</p>
      </>
    ),
  },
};

const DocumentationPage = ({ onBack }) => {
  const [activeSection, setActiveSection] = useState('getting-started');

  return (
    <div className="documentation-page">
      <div className="documentation-header">
        <button className="back-button" onClick={onBack}>← Back</button>
        <div className="documentation-title-section">
          <HomeButton onClick={onBack} />
          <span className="documentation-icon-large">📖</span>
          <h1 className="documentation-title">Documentation</h1>
        </div>
      </div>
      <div className="documentation-layout">
        <nav className="documentation-nav">
          {sections.map(s => (
            <button
              key={s.id}
              className={`doc-nav-item ${activeSection === s.id ? 'active' : ''}`}
              onClick={() => setActiveSection(s.id)}
            >
              <span className="doc-nav-icon">{s.icon}</span>
              {s.label}
            </button>
          ))}
        </nav>
        <div className="documentation-content">
          {sectionContent[activeSection] && (
            <>
              <h2 className="doc-section-title">{sectionContent[activeSection].title}</h2>
              <div className="doc-section-body">
                {sectionContent[activeSection].content}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentationPage;
